/**
 * M3X.WORKSPACE F.1 — Rail 1 (72px module rail).
 *
 * Per BUILD_READINESS_M3X_WORKSPACE_FULL.md §0.1:
 *   - 72px wide, full-height, left-aligned
 *   - 7 module buttons in the locked IA order
 *   - Brand-gradient active accent (deep / blue / purple)
 *   - ⌘1-⌘7 shortcuts (wired in F.9)
 *   - RTL stays on the left (avatar/profile convention)
 *
 * No coupling to AppShell. Used by `WorkspaceShell` (F.1) and the
 * `/me` shell (F.31).
 */

import {
  Bot,
  Briefcase,
  ChartLine,
  Home,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";


export interface ModuleEntry {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  pathPrefix: string;
  /** Optional attention dot (count > 0 surfaces an amber dot) */
  attentionCount?: number;
}


/**
 * The 7-module IA. Locked order per DESIGN_SYSTEM.md §1.
 */
export const RAIL_1_MODULES: ModuleEntry[] = [
  {
    id: "mission_control",
    labelKey: "nav.mission_control",
    icon: LayoutDashboard,
    pathPrefix: "/mission-control",
  },
  {
    id: "people",
    labelKey: "nav.people",
    icon: Users,
    pathPrefix: "/people",
  },
  {
    id: "operations",
    labelKey: "nav.operations",
    icon: Briefcase,
    pathPrefix: "/operations",
  },
  {
    id: "policies",
    labelKey: "nav.policies",
    icon: ShieldCheck,
    pathPrefix: "/policies",
  },
  {
    id: "ai",
    labelKey: "nav.ai",
    icon: Bot,
    pathPrefix: "/ai",
  },
  {
    id: "reports",
    labelKey: "nav.reports",
    icon: ChartLine,
    pathPrefix: "/reports",
  },
  {
    id: "settings",
    labelKey: "nav.settings",
    icon: Settings,
    pathPrefix: "/settings",
  },
];


/**
 * Resolve the active module from a path. Exported for `useWorkspace`
 * (F.5) to share the same source of truth.
 */
export function moduleForPath(pathname: string): ModuleEntry | null {
  // Prefer the most-specific match (longest pathPrefix) so
  // `/operations/transactions/travel` matches `/operations` not `/`.
  const sorted = [...RAIL_1_MODULES].sort(
    (a, b) => b.pathPrefix.length - a.pathPrefix.length,
  );
  return sorted.find((m) => pathname.startsWith(m.pathPrefix)) ?? null;
}


interface Rail1Props {
  /** Override which module is active. Defaults to derivation from URL. */
  activeModuleId?: string;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2 PR 6 — module-id → attention
   * boolean. When true, Rail 1 renders a 6px amber dot at top-right
   * of the module button. Driven by useAttentionSignals + the
   * registry's per-screen countSource() callbacks.
   *
   * Keys use Rail 1's underscore convention ("mission_control",
   * "people", "operations", ...). Module ids that aren't in
   * RAIL_1_MODULES are ignored.
   */
  attentionByModule?: Record<string, boolean>;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2 PR 8 — /me collapsed variant.
   * When set, Rail 1 renders a SINGLE Home icon → meHomeTarget
   * (default /mission-control) instead of the 7 module icons.
   * Switched on by WorkspaceShell when pathname.startsWith('/me').
   */
  meMode?: boolean;
  /** Where the Home icon navigates. Defaults to /mission-control. */
  meHomeTarget?: string;
}


export function Rail1({
  activeModuleId,
  attentionByModule = {},
  meMode = false,
  meHomeTarget = "/mission-control",
}: Rail1Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const active =
    activeModuleId ?? moduleForPath(location.pathname)?.id ?? null;

  // M3X.WORKSPACE-CLOSURE Session 2 PR 8 — /me collapse variant
  if (meMode) {
    return (
      <nav
        aria-label="Personal hub"
        data-rail1
        className="flex w-[72px] flex-col items-center gap-1 py-3"
        style={{ backgroundColor: "#1e1b4b" }}
      >
        <Link
          to={meHomeTarget}
          aria-label={t("workspace.me.tooltip", "Return to main workspace")}
          title={t("workspace.me.tooltip", "Return to main workspace")}
          data-me-home
          className="group relative flex h-14 w-14 flex-col items-center justify-center rounded-xl text-white transition-colors"
        >
          {/* M3X.WORKSPACE-CLOSURE Session 2.6 PR 1 — inset diagonal wash.
              Pointer-events-none + inset-0 so it doesn't bleed outside
              the tile's border-radius. z-0 (background layer). */}
          <span
            aria-hidden
            data-accent-wash
            className="pointer-events-none absolute inset-0 z-0 rounded-[10px]"
            style={{
              background:
                "linear-gradient(135deg, rgba(0,211,194,0.18) 0%, rgba(168,70,191,0.15) 100%)",
            }}
          />
          {/* M3X.WORKSPACE-CLOSURE Session 2.8 PR 3 — strip widened
              (3→4px) + inset reduced (8→4px). Reads as a confident
              vertical anchor rather than a floating tick. */}
          <span
            aria-hidden
            data-accent-strip
            className="absolute start-0 z-[1]"
            style={{
              width: "4px",
              top: "4px",
              bottom: "4px",
              borderEndStartRadius: "0",
              borderStartStartRadius: "0",
              borderEndEndRadius: "4px",
              borderStartEndRadius: "4px",
              background:
                "linear-gradient(180deg, #00D3C2 0%, #2260C9 50%, #A846BF 100%)",
            }}
          />
          <Home className="relative z-[2] h-5 w-5" />
          <span
            className="relative z-[2] mt-0.5 leading-none"
            style={{ fontSize: "9px", color: "white" }}
          >
            {t("workspace.me.back", "Back")}
          </span>
        </Link>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Modules"
      data-rail1
      // M3X.WORKSPACE-CLOSURE Session 2.5 PR 1 — locked palette §0.8:
      // Rail 1 background is the deep indigo #1e1b4b.
      // RTL note: Rail 1 always sits on the start side via `inset-inline-start`
      // (the existing app already uses tailwindcss-rtl). 72px = w-[72px].
      className="flex w-[72px] flex-col items-center gap-1 py-3"
      style={{ backgroundColor: "#1e1b4b" }}
    >
      {RAIL_1_MODULES.map((m) => {
        const Icon = m.icon;
        const isActive = active === m.id;
        return (
          <Link
            key={m.id}
            to={m.pathPrefix}
            aria-label={t(m.labelKey, m.id)}
            aria-current={isActive ? "page" : undefined}
            data-module={m.id}
            data-active-module={isActive ? m.id : undefined}
            className={[
              "group relative flex h-14 w-14 flex-col items-center justify-center",
              "rounded-xl transition-colors",
              isActive ? "text-white" : "hover:text-white",
            ].join(" ")}
            style={
              isActive
                ? {
                    color: "white",
                    // M3X.WORKSPACE-CLOSURE FINAL PR 3 — belt-and-braces.
                    // Tailwind's `relative` class is on the className list
                    // but if anything purges it or a parent stylesheet
                    // overrides, an inline `position: relative` guarantees
                    // the absolute-positioned strip stays bounded to THIS
                    // tile (no full-rail vertical bleed). `overflow:hidden`
                    // is the final safety so even a misconfigured strip
                    // can't escape the tile.
                    position: "relative",
                    overflow: "hidden",
                  }
                : { color: "#c7d2fe" }
            }
            onMouseEnter={(e) => {
              if (!isActive) {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "rgba(255,255,255,0.08)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                (e.currentTarget as HTMLElement).style.backgroundColor = "";
              }
            }}
          >
            {/* M3X.WORKSPACE-CLOSURE Session 2.6 PR 1 — clean active treatment:
                two absolute-positioned layers below the icon, both contained
                inside the tile's border-radius. NO horizontal gradient on
                the tile background (that's what produced the bleed-from-
                outer-edge artifact in LTR mode). */}
            {isActive && (
              <>
                {/* Wash layer (z-0) — subtle diagonal, inset-0, clipped by
                    parent's rounded-xl. pointer-events-none so clicks pass
                    through to the Link. */}
                <span
                  aria-hidden
                  data-accent-wash
                  className="pointer-events-none absolute inset-0 z-0 rounded-[10px]"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(0,211,194,0.18) 0%, rgba(168,70,191,0.15) 100%)",
                  }}
                />
                {/* M3X.WORKSPACE-CLOSURE Session 2.8 PR 3 — strip
                    widened (3→4px) + inset reduced (8→4px) so it reads
                    as a confident vertical anchor. Outer edge flush
                    with rail; inner edge rounded 4px. RTL-correct via
                    `start-0` logical class. */}
                <span
                  aria-hidden
                  data-accent-strip
                  className="absolute start-0 z-[1]"
                  style={{
                    width: "4px",
                    top: "4px",
                    bottom: "4px",
                    borderEndStartRadius: "0",
                    borderStartStartRadius: "0",
                    borderEndEndRadius: "4px",
                    borderStartEndRadius: "4px",
                    background:
                      "linear-gradient(180deg, #00D3C2 0%, #2260C9 50%, #A846BF 100%)",
                  }}
                />
              </>
            )}
            <Icon className="relative z-[2] h-5 w-5" />
            <span
              className="relative z-[2] mt-0.5 leading-none"
              style={{
                fontSize: "9px",
                color: isActive ? "white" : "#c7d2fe",
              }}
            >
              {t(m.labelKey, m.id).split(" ")[0]}
            </span>
            {/* M3X.WORKSPACE-CLOSURE Session 2 PR 6 — amber 6×6 dot when
                the module has any sub-domain with a non-zero count. */}
            {attentionByModule[m.id] && (
              <span
                data-attention-dot
                aria-hidden
                className="absolute"
                style={{
                  top: "8px",
                  insetInlineEnd: "12px",
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#f59e0b",
                }}
              />
            )}
            {/* Legacy fallback (kept for callers passing m.attentionCount
                via the static RAIL_1_MODULES table). */}
            {m.attentionCount && m.attentionCount > 0 && (
              <span
                className="absolute top-1 end-1 h-1.5 w-1.5 rounded-full bg-amber-500"
                aria-label={`${m.attentionCount} items need attention`}
              />
            )}
          </Link>
        );
      })}
    </nav>
  );
}


export default Rail1;
